import {
  FrequencyUnit,
  HabitSchedule,
  ScheduleEntryState,
} from '@griply/types';
import {
  addDays,
  addMilliseconds,
  addMonths,
  addWeeks,
  endOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { weekdaysNumberMap } from '../constants';
import { getScheduleTimeframeCompletion } from '../get-schedule-timeframe-completion';
import { getUniqueDayEntries } from '../get-unique-day-entries';
import {
  getPlannedTimeframeDueDateBySchedule,
  getPlannedTimeframeOccurrences,
} from './get-planned-timeframe-due-date-by-schedule';
import { getScheduleTimeframeByDate } from './get-schedule-timeframe-by-date';
import { isScheduleDue } from './is-schedule-due';
import { parseIntervalFromRrule } from './parse-rrule';
import { Options } from './types';

/**
 * get the next due date based on the schedule frequency without looking at the rescheduled dates in `plannedOccurrences`
 */
export const getFrequencyBaseDueDateBySchedule = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: Options,
) => {
  if (isScheduleDue(schedule, { today, weekStartsOn })) {
    return endOfDay(today);
  }

  // get the completions data for the schedule timeframe
  const {
    completions: completionsCurrentTimeframe,
    totalUniqueDays: totalCurrentTimeframe,
  } = getScheduleTimeframeCompletion(schedule, {
    referenceDate: today,
    weekStartsOn,
  });

  const uniqueDayCompletionsInTimeframe = getUniqueDayEntries(
    completionsCurrentTimeframe.map(({ date }) => date),
  );
  const interval = parseIntervalFromRrule(schedule.rrule.format);

  const nextCompletionDate =
    uniqueDayCompletionsInTimeframe.length < totalCurrentTimeframe
      ? endOfDay(addDays(today, 1))
      : endOfDay(
          schedule.frequency?.unit === FrequencyUnit.Week
            ? addWeeks(
                startOfWeek(today, {
                  weekStartsOn: weekdaysNumberMap[weekStartsOn],
                }),
                interval,
              )
            : addMonths(startOfMonth(today), interval),
        );

  const meaningfulEntries = schedule.entries.filter(
    ({ state, value }) =>
      (state === ScheduleEntryState.Complete && !!value) ||
      state !== ScheduleEntryState.Complete,
  );
  const lastEntryDate = meaningfulEntries.length
    ? new Date(Math.max(...meaningfulEntries.map(({ date }) => date.getTime())))
    : undefined;

  return new Date(
    Math.max(
      nextCompletionDate.getTime(),
      ...(lastEntryDate ? [endOfDay(addDays(lastEntryDate, 1)).getTime()] : []),
      schedule.startDate.getTime(),
    ),
  );
};

export const getFrequencyDueDateBySchedule = (
  schedule: HabitSchedule,
  { today, weekStartsOn }: Options,
) => {
  // check current timeframe on planned occurrences
  const hasPlannedOccurrences = !!getPlannedTimeframeOccurrences(schedule, {
    weekStartsOn,
    today,
  }).length;
  const plannedOccurrenceDate = hasPlannedOccurrences
    ? getPlannedTimeframeDueDateBySchedule(schedule, {
        weekStartsOn,
        today,
      })
    : undefined;

  // When there is no plannedOccurrence in the current timeframe, check if the previous timeframe has overdue planned occurrences
  if (!plannedOccurrenceDate) {
    // check if previous timeframe has overdue planned occurrences
    const timeframe = getScheduleTimeframeByDate(schedule, today, weekStartsOn);
    const prevTimeframeDate = addMilliseconds(timeframe.startDate, -1);
    const hasPreviousTimeframePlannedOccurrences =
      !!getPlannedTimeframeOccurrences(schedule, {
        weekStartsOn,
        today: prevTimeframeDate,
      }).length;

    const dueDatePrevTimeframe = hasPreviousTimeframePlannedOccurrences
      ? getPlannedTimeframeDueDateBySchedule(schedule, {
          weekStartsOn,
          today: prevTimeframeDate,
        })
      : undefined;

    if (dueDatePrevTimeframe) {
      return dueDatePrevTimeframe;
    }
  }

  if (hasPlannedOccurrences) {
    return plannedOccurrenceDate;
  }

  // check the regular way of frequency due date
  return getFrequencyBaseDueDateBySchedule(schedule, {
    today,
    weekStartsOn,
  });
};
